import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { useNavigate, useLocation } from "react-router-dom";
import { LessonTypeColor } from "constants/CommonConstants";

const DEFAULT_COURSE = {
  courseName: { value: "Energy Management Basics" },
  category: { value: "Energy & Utilities" },
  level: { value: "Beginner" },
  courseLanguage: { value: "English" },
  courseDescription: {
    value:
      "Energy Management Basics introduces the fundamental concepts of efficient energy use in organizations and facilities. This course covers key principles such as energy consumption patterns, cost-saving strategies, sustainability practices, and basic monitoring techniques. Learners will gain an understanding of how effective energy management helps reduce operational costs, improve efficiency, and support environmental responsibility.",
  },
  preRequisites: { value: "YES" },
  requiredDoc: {
    value: [
      { value: "10th_grade_ssc", label: "10th Grade / SSC" },
      { value: "12th_grade", label: "12th Grade" },
      { value: "diploma_certificate", label: "Diploma Certificate" },
    ],
  },
  certificateEnabled: { value: "YES" },
  courseMedia: { value: true },
};
const DEFAULT_SESSIONS = [
  {
    sessionName: { value: "Energy Basics & Overview" },
    sessionDate: { value: "2026-02-10" },
    sessionStartTime: { value: "10:00" },
    sessionEndTime: { value: "12:00" },
    trainerName: "Amit Sharma",
    sessionDescription: {
      value: "<p>Live introduction session covering fundamentals and Q&A.</p>",
    },
  },
  {
    sessionName: { value: "Practical Energy Optimization" },
    sessionDate: { value: "2026-02-12" },
    sessionStartTime: { value: "11:00" },
    sessionEndTime: { value: "13:30" },
    trainerName: "Neha Verma",
    sessionDescription: {
      value: "<p>Hands-on examples and real-world optimization techniques.</p>",
    },
  },
];

function CoursePreview() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const courseData = {
    ...DEFAULT_COURSE,
    // ...state?.courseData,  //when api will integrate get data from state
  };

  const lessons = state?.lessons || [];
  const sessions = state?.sessions || DEFAULT_SESSIONS;

  if (!courseData) return null;

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col>
            <Card>
              {/* Header */}
              <CardHeader className="d-flex align-items-center">
                <i
                  className="fa-solid fa-angle-left mr-2 cursor"
                  onClick={() => navigate(-1)}
                />
                <h3 className="mb-0">Course Details Preview</h3>
              </CardHeader>

              <CardBody>
                <Row className="mb-4">
                  {/* Title & Description + category +level + language */}
                  <Col md="7">
                    <h2>{courseData.courseName.value}</h2>
                    <p>{courseData.courseDescription.value}</p>

                    <div className="mt-5">
                      <div className="d-flex align-items-center mb-2">
                        <strong className="mr-1">Category:</strong>
                        <span>{courseData.category.value}</span>
                      </div>

                      <div className="d-flex align-items-center mb-2">
                        <strong className="mr-1">Level:</strong>
                        <span>{courseData.level.value}</span>
                      </div>

                      <div className="d-flex align-items-center">
                        <strong className="mr-1">Language:</strong>
                        <span>{courseData.courseLanguage.value}</span>
                      </div>
                    </div>
                  </Col>

                  {/*Thumbnail / Video */}
                  <Col md="5">
                    {courseData.courseMedia?.value && (
                      <img
                        src={require("../../assets/img/certificates/energy_management.jpg")}
                        alt="Course Thumbnail"
                        className="img-fluid rounded"
                      />
                    )}
                  </Col>
                </Row>

                {/*Prerequisites */}
                <Row className="mb-4">
                  <Col>
                    <h3>Prerequisites</h3>
                    {courseData.preRequisites.value === "YES" ? (
                      <ol className="pl-3">
                        {courseData.requiredDoc.value.map((doc) => (
                          <li key={doc.value}>{doc.label}</li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-muted">No prerequisites required</p>
                    )}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <h3>Lessons</h3>

                    {lessons.length === 0 ? (
                      <p className="text-muted">No lessons added</p>
                    ) : (
                      lessons.map((lesson, index) => (
                        <Card
                          key={index}
                          className="lesson-card mb-3 shadow-sm w-75"
                        >
                          <CardBody className="py-2">
                            <div className="d-flex justify-content-start align-items-center">
                              <strong>
                                {/* {index + 1}. {lesson.title} */}
                                {lesson.title}
                              </strong>

                              <div>
                                <span className="ml-5 mr-2 text-muted">
                                  {lesson.duration}
                                </span>
                                <Badge color={LessonTypeColor[lesson.type]}>
                                  {lesson.type}
                                </Badge>
                              </div>
                            </div>
                            <hr className="my-2" />

                            {lesson.description && (
                              <p className="mt-2 mb-0 text-muted text-justify">
                                {lesson.description}
                              </p>
                            )}
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </Col>
                </Row>
                {/* Live Sessions */}
                <Row className="mb-4">
                  <Col>
                    <h3>Live Sessions</h3>

                    {sessions.length === 0 ? (
                      <p className="text-muted">No live sessions scheduled</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table ">
                          <thead className="thead-light">
                            <tr>
                              <th>Sr no</th>
                              <th>Session Name</th>
                              <th>Date</th>
                              <th>Time</th>
                              <th>Instructor</th>
                              <th>Description</th>
                            </tr>
                          </thead>

                          <tbody>
                            {sessions.map((session, index) => (
                              <>
                                {/* Main Row */}
                                <tr>
                                  <td>{index + 1}</td>
                                  <td>{session.sessionName.value}</td>
                                  <td>{session.sessionDate.value}</td>
                                  <td>
                                    {session.sessionStartTime.value} –{" "}
                                    {session.sessionEndTime.value}
                                  </td>
                                  <td>{session.trainerName}</td>
                                  <td>
                                    {/* Description Row */}
                                    {session.sessionDescription?.value && (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            session.sessionDescription.value,
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                              </>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <h3>Certificate</h3>

                    {courseData.certificateEnabled.value === "YES" ? (
                      <>
                        <Badge color="success" className="mb-3">
                          Available
                        </Badge>

                        <div className="mt-3">
                          <img
                            src={require("../../assets/img/certificates/template1.png")}
                            alt="Certificate Preview"
                            className="img-fluid rounded shadow-sm w-25"
                          />
                        </div>
                      </>
                    ) : (
                      <Badge color="secondary">Not Available</Badge>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default CoursePreview;
